/**
 * O trecho apontado na mesa (E202.F2, GUS-145, ADR-0145 §9; E184.F4): `/mesa?saida=&trecho=` abre a
 * saída, acha o trecho que contém o texto do apontamento (`trechoApontado`), abre o editor nele uma
 * vez e rola até ele. Fora dos editáveis, o aviso diz o caminho que o corrige (E184.F4.C03).
 */
import { useEffect, useRef } from 'react';

import { ptBR } from '@/i18n/pt-BR';
import type { SaidaDaEdicao } from '@/lib/correcaoDoRascunho';
import type { DocumentoDaSaidaData } from '@/lib/documentoDaSaida';

import { trechoApontado } from './buscaDoApontado';
import type { EdicaoNoDocumento } from './useEdicaoNoDocumento';

const t = ptBR.documentoDaMesa.apontado;
const TENTATIVAS_DE_ROLAR = 10;
const tm = ptBR.editorManual.apontado;

export function TrechoApontado({
  procurado,
  documento,
  edicao,
  saida,
}: {
  procurado: string | null;
  documento: DocumentoDaSaidaData | null;
  edicao: EdicaoNoDocumento | null;
  saida: SaidaDaEdicao;
}): JSX.Element | null {
  const aberto = useRef(false);
  const lista = documento?.trechos ?? null;
  const achado = procurado !== null && lista !== null ? trechoApontado(lista, procurado) : null;
  const abrir = edicao?.abrir;
  useEffect(() => {
    if (aberto.current || achado === null) return;
    aberto.current = true;
    abrir?.(achado.id);
    // E205.F2: o trecho pode estar noutra seção; a mesa troca de seção e o bloco aparece depois.
    let tentativas = 0;
    const rolar = (): void => {
      const bloco = document.querySelector(`[data-trecho="${achado.id}"]`);
      if (bloco !== null) {
        bloco.scrollIntoView?.({ block: 'center' });
        return;
      }
      tentativas += 1;
      if (tentativas < TENTATIVAS_DE_ROLAR) window.setTimeout(rolar, 100);
    };
    window.setTimeout(rolar, 0);
  }, [achado, abrir]);
  if (procurado === null || lista === null) return null;
  return (
    <p
      role="status"
      className={achado !== null ? 'text-body-sm text-fg' : 'text-body-sm text-warning-700'}
      data-testid="trecho-apontado"
      data-achado={achado !== null ? 'sim' : 'nao'}
    >
      {achado !== null ? t.aberto : tm.naoLocalizado[saida].replace('{trecho}', procurado)}
    </p>
  );
}
